import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Stack,
  Group,
  Button,
  Text,
  Table,
  ActionIcon,
  Menu,
  Badge,
  TextInput,
  LoadingOverlay,
  Modal,
  Code,
  ScrollArea,
  Collapse,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconPlus,
  IconDots,
  IconEdit,
  IconTrash,
  IconCopy,
  IconPlayerPlay,
  IconSearch,
  IconFileSpreadsheet,
  IconFileTypePdf,
  IconEye,
  IconChevronDown,
  IconChevronRight,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useOrganization } from '@/hooks/useOrganization';
import {
  useCustomReports,
  useDeleteCustomReport,
  useDuplicateCustomReport,
  useExecuteReport,
} from '@/hooks/api/useCustomReports';
import { exportReportToPdf } from '@/lib/reportHtmlTemplate';
import type { CustomReport } from '@/hooks/api/useCustomReports';

export function CustomReportsPage() {
  const { organizationId } = useOrganization();
  const { data: reports = [], isLoading } = useCustomReports(organizationId);
  const deleteReport = useDeleteCustomReport();
  const duplicateReport = useDuplicateCustomReport();
  const executeReport = useExecuteReport();
  const location = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [resultsOpened, setResultsOpened] = useState(false);
  const [reportResults, setReportResults] = useState<any>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const filteredReports = reports.filter((report) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      report.name.toLowerCase().includes(searchLower) ||
      report.description?.toLowerCase().includes(searchLower)
    );
  });

  const handleCreateReport = () => {
    navigate('/reports/create');
  };

  const handleEditReport = (report: CustomReport) => {
    navigate(`/reports/edit/${report.id}`, { state: { editingReport: report } });
  };

  const handleDeleteReport = (report: CustomReport) => {
    modals.openConfirmModal({
      title: 'Delete Report',
      children: (
        <Text size="sm">
          Are you sure you want to delete the report "{report.name}"? This action cannot be
          undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteReport.mutateAsync(report.id);
          notifications.show({
            title: 'Report Deleted',
            message: 'The report has been deleted successfully',
            color: 'green',
          });
        } catch (error: any) {
          notifications.show({
            title: 'Error',
            message: error.message || 'Failed to delete report',
            color: 'red',
          });
        }
      },
    });
  };

  const handleDuplicateReport = async (report: CustomReport) => {
    try {
      await duplicateReport.mutateAsync(report.id);
      notifications.show({
        title: 'Report Duplicated',
        message: `Created a copy of "${report.name}"`,
        color: 'green',
      });
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to duplicate report',
        color: 'red',
      });
    }
  };

  const handleRunReport = async (report: CustomReport) => {
    try {
      const result = await executeReport.mutateAsync(report.id);
      setReportResults(result);
      setResultsOpened(true);
      setExpandedGroups(new Set()); // Reset expanded groups

      notifications.show({
        title: 'Report Generated',
        message: `Found ${result.data.length} results`,
        color: 'green',
      });
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to run report',
        color: 'red',
      });
    }
  };

  // Group data and calculate aggregations in memory
  const groupedData = useMemo(() => {
    if (!reportResults || !reportResults.config.groupBy || reportResults.config.groupBy.length === 0) {
      return null;
    }

    const { data, config } = reportResults;
    const groupByFields = config.groupBy.map((g: any) => g.field);
    const groups = new Map<string, any[]>();

    // Group rows by the group-by fields
    data.forEach((row: any) => {
      const groupKey = groupByFields.map((field: string) => row[field] ?? 'null').join('|||');
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(row);
    });

    // Calculate aggregations for each group
    const result: Array<{
      groupKey: string;
      groupValues: Record<string, any>;
      rows: any[];
      aggregations: Record<string, any>;
    }> = [];

    groups.forEach((rows, groupKey) => {
      const groupValues: Record<string, any> = {};
      groupByFields.forEach((field: string, index: number) => {
        groupValues[field] = rows[0][field];
      });

      const aggregations: Record<string, any> = {};
      if (config.aggregations) {
        config.aggregations.forEach((agg: any) => {
          if (agg.type === 'count') {
            aggregations[agg.alias] = rows.length;
          } else if (agg.type === 'sum') {
            aggregations[agg.alias] = rows.reduce((sum, row) => sum + (Number(row[agg.field]) || 0), 0);
          } else if (agg.type === 'avg') {
            const sum = rows.reduce((sum, row) => sum + (Number(row[agg.field]) || 0), 0);
            aggregations[agg.alias] = rows.length > 0 ? sum / rows.length : 0;
          } else if (agg.type === 'min') {
            aggregations[agg.alias] = Math.min(...rows.map(row => Number(row[agg.field]) || 0));
          } else if (agg.type === 'max') {
            aggregations[agg.alias] = Math.max(...rows.map(row => Number(row[agg.field]) || 0));
          }
        });
      }

      result.push({ groupKey, groupValues, rows, aggregations });
    });

    return result;
  }, [reportResults]);

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const handleExportCSV = () => {
    if (!reportResults) return;

    const { data, config } = reportResults;
    const csvRows: string[] = [];

    if (groupedData) {
      // Export pivot table structure
      const columns = config.fields.map((f: any) => ({ key: f.field, label: f.label }));
      const groupByFields = config.groupBy.map((g: any) => g.field);

      // CSV header
      csvRows.push(columns.map(c => c.label).join(','));

      // For each group
      groupedData.forEach((group) => {
        // Group header row (summary in first column, rest empty)
        const groupLabel = groupByFields.map((field: string) => {
          const fieldConfig = config.fields.find((f: any) => f.field === field);
          const value = group.groupValues[field];
          return `${fieldConfig?.label || field}: ${value ?? 'N/A'}`;
        }).join(', ');

        // Build aggregation summary
        const aggSummary = config.aggregations?.map((agg: any) => {
          const value = group.aggregations[agg.alias];
          let displayValue = value;

          if (value !== null && value !== undefined && !isNaN(Number(value))) {
            if (agg.type === 'avg') {
              displayValue = Number(value).toFixed(2);
            } else if (agg.field === 'cost') {
              displayValue = `$${Number(value).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`;
            } else {
              displayValue = Number(value).toLocaleString('en-US');
            }
          }

          return `${agg.type.toUpperCase()}(${agg.field === '*' ? 'All' : agg.field}): ${displayValue}`;
        }).join(' | ') || '';

        // First cell has the summary, rest are empty
        const summaryText = `"${groupLabel}${aggSummary ? ' - ' + aggSummary : ''}"`;
        const emptyCells = new Array(columns.length - 1).fill('');
        csvRows.push([summaryText, ...emptyCells].join(','));

        // Detail rows for this group
        group.rows.forEach((row: any) => {
          const rowValues = columns.map((col) => {
            const value = row[col.key];
            if (value === null || value === undefined) return '';
            const str = String(value);
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
              return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
          });
          csvRows.push(rowValues.join(','));
        });

        // Empty row between groups
        csvRows.push('');
      });
    } else {
      // Standard export (no grouping)
      const columns = config.fields.map((f: any) => ({ key: f.field, label: f.label }));

      // CSV header
      csvRows.push(columns.map(c => c.label).join(','));

      // Data rows
      data.forEach((row: any) => {
        const rowValues = columns.map((col) => {
          const value = row[col.key];
          if (value === null || value === undefined) return '';
          const str = String(value);
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        });
        csvRows.push(rowValues.join(','));
      });
    }

    const csv = csvRows.join('\n');

    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportResults.report.name.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    notifications.show({
      title: 'Export Complete',
      message: 'CSV file downloaded successfully',
      color: 'green',
    });
  };

  const handleExportPDF = () => {
    if (!reportResults) return;

    exportReportToPdf(
      {
        name: reportResults.report.name,
        config: reportResults.config,
        data: reportResults.data,
      },
      {
        title: reportResults.report.name,
        subtitle: reportResults.report.description,
        includeSummary: true,
      }
    );

    notifications.show({
      title: 'PDF Export',
      message: 'Print dialog opened. Save as PDF to export.',
      color: 'blue',
    });
  };

  return (
    <Container fluid py="xl">
      <Stack gap="lg">
        <Group justify="apart">
          <div>
            <Title order={1}>Saved Reports</Title>
            <Text size="sm" c="dimmed" mt={4}>
              View, manage, and run your saved custom reports
            </Text>
          </div>

          <Button leftSection={<IconPlus size={16} />} onClick={handleCreateReport}>
            Create Report
          </Button>
        </Group>

        {/* Search Bar */}
        <Paper p="md" withBorder>
          <TextInput
            placeholder="Search reports..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        </Paper>

        {/* Reports Table */}
        <Paper p="md" withBorder pos="relative">
          <LoadingOverlay visible={isLoading} />

          {filteredReports.length === 0 ? (
            <Stack align="center" py="xl">
              <IconFileSpreadsheet size={48} stroke={1.5} style={{ color: 'var(--mantine-color-dimmed)' }} />
              <Text c="dimmed">
                {search
                  ? 'No reports match your search'
                  : 'No custom reports yet. Create one to get started.'}
              </Text>
            </Stack>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Entity</Table.Th>
                  <Table.Th>Fields</Table.Th>
                  <Table.Th>Last Run</Table.Th>
                  <Table.Th>Runs</Table.Th>
                  <Table.Th>Shared</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredReports.map((report) => (
                  <Table.Tr key={report.id}>
                    <Table.Td>
                      <div>
                        <Text fw={500}>{report.name}</Text>
                        {report.description && (
                          <Text size="xs" c="dimmed">
                            {report.description}
                          </Text>
                        )}
                      </div>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" size="sm">
                        {report.config.entities[0]}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{report.config.fields.length} fields</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {report.last_run_at
                          ? new Date(report.last_run_at).toLocaleDateString()
                          : 'Never'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{report.run_count}</Text>
                    </Table.Td>
                    <Table.Td>
                      {report.is_shared ? (
                        <Badge size="sm" color="blue" variant="light">
                          Shared
                        </Badge>
                      ) : (
                        <Text size="sm" c="dimmed">
                          Private
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" justify="flex-end">
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          onClick={() => handleRunReport(report)}
                          loading={executeReport.isPending}
                          title="Run Report"
                        >
                          <IconPlayerPlay size={16} />
                        </ActionIcon>

                        <Menu position="bottom-end" shadow="md">
                          <Menu.Target>
                            <ActionIcon variant="subtle" color="gray">
                              <IconDots size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconEdit size={16} />}
                              onClick={() => handleEditReport(report)}
                            >
                              Edit
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconCopy size={16} />}
                              onClick={() => handleDuplicateReport(report)}
                            >
                              Duplicate
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item
                              leftSection={<IconTrash size={16} />}
                              color="red"
                              onClick={() => handleDeleteReport(report)}
                            >
                              Delete
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>

        {/* Results Modal */}
        <Modal
          opened={resultsOpened}
          onClose={() => setResultsOpened(false)}
          title={<Title order={3}>Report Results</Title>}
          size="xl"
        >
          {reportResults && (
            <Stack gap="md">
              <Group justify="apart">
                <div>
                  <Text fw={500}>{reportResults.report.name}</Text>
                  <Text size="sm" c="dimmed">
                    {reportResults.data.length} results found
                  </Text>
                </div>
                <Group gap="xs">
                  <Button
                    variant="light"
                    leftSection={<IconFileSpreadsheet size={16} />}
                    onClick={handleExportCSV}
                  >
                    Export CSV
                  </Button>
                  <Button
                    variant="light"
                    leftSection={<IconFileTypePdf size={16} />}
                    onClick={handleExportPDF}
                  >
                    Export PDF
                  </Button>
                </Group>
              </Group>

              <ScrollArea h={400}>
                {groupedData ? (
                  // Pivot table view with collapsible groups
                  <Stack gap="xs">
                    {groupedData.map((group) => {
                      const isExpanded = expandedGroups.has(group.groupKey);
                      const groupFields = reportResults.config.groupBy.map((g: any) => g.field);

                      return (
                        <Paper key={group.groupKey} withBorder>
                          {/* Group Header with Aggregations */}
                          <Group
                            p="sm"
                            justify="space-between"
                            style={{ cursor: 'pointer', backgroundColor: 'var(--mantine-color-gray-0)' }}
                            onClick={() => toggleGroup(group.groupKey)}
                          >
                            <Group gap="md">
                              <ActionIcon variant="subtle" size="sm">
                                {isExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                              </ActionIcon>

                              {/* Group field values */}
                              {groupFields.map((field: string) => {
                                const fieldConfig = reportResults.config.fields.find((f: any) => f.field === field);
                                const value = group.groupValues[field];
                                let displayValue = value;

                                // Format based on field type
                                if (fieldConfig?.format === 'currency' && value !== null) {
                                  displayValue = `$${Number(value).toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}`;
                                } else if (fieldConfig?.format === 'date' && value) {
                                  displayValue = new Date(value).toLocaleDateString();
                                } else if (typeof value === 'boolean') {
                                  displayValue = value ? 'Yes' : 'No';
                                } else if (value === null || value === undefined) {
                                  displayValue = 'N/A';
                                }

                                return (
                                  <div key={field}>
                                    <Text size="xs" c="dimmed">{fieldConfig?.label || field}</Text>
                                    <Text fw={600}>{String(displayValue)}</Text>
                                  </div>
                                );
                              })}
                            </Group>

                            {/* Aggregation values */}
                            <Group gap="lg">
                              {reportResults.config.aggregations?.map((agg: any) => {
                                const value = group.aggregations[agg.alias];
                                let displayValue = value;

                                // Format numeric aggregations
                                if (value !== null && value !== undefined && !isNaN(Number(value))) {
                                  if (agg.type === 'avg') {
                                    displayValue = Number(value).toFixed(2);
                                  } else if (agg.field === 'cost') {
                                    displayValue = `$${Number(value).toLocaleString('en-US', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}`;
                                  } else {
                                    displayValue = Number(value).toLocaleString('en-US');
                                  }
                                }

                                return (
                                  <div key={agg.alias}>
                                    <Text size="xs" c="dimmed">
                                      {agg.type.toUpperCase()}({agg.field === '*' ? 'All' : agg.field})
                                    </Text>
                                    <Text fw={600} c="blue">{String(displayValue)}</Text>
                                  </div>
                                );
                              })}
                            </Group>
                          </Group>

                          {/* Detail Rows (Collapsible) */}
                          <Collapse in={isExpanded}>
                            <Table striped highlightOnHover>
                              <Table.Thead>
                                <Table.Tr>
                                  {reportResults.config.fields.map((field: any, index: number) => (
                                    <Table.Th key={index}>{field.label}</Table.Th>
                                  ))}
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {group.rows.map((row: any, rowIndex: number) => (
                                  <Table.Tr key={rowIndex}>
                                    {reportResults.config.fields.map((field: any, colIndex: number) => {
                                      const value = row[field.field];
                                      let displayValue = value;

                                      // Format based on field type
                                      if (field.format === 'currency' && value !== null) {
                                        displayValue = `$${Number(value).toLocaleString('en-US', {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}`;
                                      } else if (field.format === 'date' && value) {
                                        displayValue = new Date(value).toLocaleDateString();
                                      } else if (field.format === 'datetime' && value) {
                                        displayValue = new Date(value).toLocaleString();
                                      } else if (typeof value === 'boolean') {
                                        displayValue = value ? 'Yes' : 'No';
                                      } else if (value === null || value === undefined) {
                                        displayValue = '-';
                                      }

                                      return (
                                        <Table.Td key={colIndex}>
                                          <Text size="sm">{String(displayValue)}</Text>
                                        </Table.Td>
                                      );
                                    })}
                                  </Table.Tr>
                                ))}
                              </Table.Tbody>
                            </Table>
                          </Collapse>
                        </Paper>
                      );
                    })}
                  </Stack>
                ) : (
                  // Standard table view (no grouping)
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        {reportResults.config.fields.map((field: any, index: number) => (
                          <Table.Th key={index}>{field.label}</Table.Th>
                        ))}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {reportResults.data.map((row: any, rowIndex: number) => (
                        <Table.Tr key={rowIndex}>
                          {reportResults.config.fields.map((field: any, colIndex: number) => {
                            const value = row[field.field];
                            let displayValue = value;

                            // Format based on field type
                            if (field.format === 'currency' && value !== null) {
                              displayValue = `$${Number(value).toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`;
                            } else if (field.format === 'date' && value) {
                              displayValue = new Date(value).toLocaleDateString();
                            } else if (field.format === 'datetime' && value) {
                              displayValue = new Date(value).toLocaleString();
                            } else if (typeof value === 'boolean') {
                              displayValue = value ? 'Yes' : 'No';
                            } else if (value === null || value === undefined) {
                              displayValue = '-';
                            }

                            return (
                              <Table.Td key={colIndex}>
                                <Text size="sm">{String(displayValue)}</Text>
                              </Table.Td>
                            );
                          })}
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </ScrollArea>
            </Stack>
          )}
        </Modal>
      </Stack>
    </Container>
  );
}
